import {
  OnInit,
  Component,
  ContentChildren,
  EventEmitter,
  ViewChild,
  Input,
  Output,
  QueryList,
  OnDestroy,
  input,
  booleanAttribute
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TopoTableColumnComponent } from './topo-table-column.component';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { QueryParams } from '../../store/models/query.model';

interface TableColumns {
  label: string;
  key: string;
  classes?: string;
}

@Component({
  selector: 'topo-table',
  templateUrl: './topo-table.component.html',
  styleUrls: ['./topo-table.component.scss'],
  standalone: false
})
export class TopoTableComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  @Input() horizontalScrollActive = false;
  @Input() isContained = true;
  @Input() showHeader = true;
  @Input() headerLabel: string;
  @Input() showHeaderLabel = true;
  @Input() showTableInfo = true;
  @Input() showBorders = true;
  @Input() showNewItem = true;
  @Input() newItemLabel: string;
  @Input() showSearch = true;
  @Input() showCount = true;
  @Input() showPager = true;
  @Input() searchBoxClass = null;
  @Input() useSmallHeaderLabel = false;
  @Input() enableDragDrop = false;
  @Input()
  set items(value: any) {
    this._items = value;
  }
  get items(): any {
    return this._items;
  }
  private _items: any;

  @Input() isLoading = false;
  @Input() itemName = 'Items';
  @Input() newItemText = 'New Item';
  @Input() searchPlaceholder = 'Search';
  @Input() skip = 0;
  @Input() take = 10;
  @Input() totalCount = 0;
  @Input() sortField = null;
  @Input() sortDirection = 'DESC';
  @Output() newItem = new EventEmitter<void>();
  @Output() paginate = new EventEmitter<number>();
  @Output() search = new EventEmitter<string>();
  @Output() dragDrop = new EventEmitter<any>();
  @Output() sort = new EventEmitter<{ sortField: string; sortDirection: string }>();
  @Output() checkboxSelected = new EventEmitter<boolean>();
  @Input({ transform: booleanAttribute }) hasIndependentTotalCount: boolean;
  @Input() independentTotalCount = 0;
  @Input() showChangeRowCount = false;
  @Output() takeChanged = new EventEmitter<number>();

  @Input()
  set queryParams(value: QueryParams) {
    this.skip = value?.skip;
    this.take = value?.take;
    this.totalCount = value?.totalCount;

    if (this.searchControl && !!value?.searchTerm) {
      this.lastSearchTerm = value.searchTerm;
      this.searchControl.setValue(value.searchTerm);
    }
  }
  lastSearchTerm: string;
  @ContentChildren(TopoTableColumnComponent, { descendants: true }) columns: QueryList<any>;
  searchControl: FormControl = new FormControl();

  enableSelectionHeader = input(false, { transform: booleanAttribute });
  showSelectionHeader = input(false);

  constructor() {}

  get hasSearch(): boolean {
    return this.searchControl && this.searchControl.value;
  }

  get isSearching(): boolean {
    return this.hasSearch && this.isLoading;
  }

  ngOnInit(): void {
    const sub = this.searchControl.valueChanges.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(searchTerm => {
      if (searchTerm !== this.lastSearchTerm) {
        this.lastSearchTerm = searchTerm;
        this.onSearch(searchTerm);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.newItem.complete();
    this.paginate.complete();
    this.search.complete();
    this.dragDrop.complete();
    this.sort.complete();
    this.checkboxSelected.complete();
  }

  clearSearch(): void {
    if (this.searchControl) {
      this.searchControl.setValue('');
    }
  }

  onPaginate(skip: number): void {
    this.paginate.emit(skip);
  }

  onTakeChanged(take: number): void {
    this.takeChanged.emit(take);
  }

  onSearch(searchTerm: string): void {
    this.search.emit(searchTerm);
  }

  onCheckboxSelected(event) {
    this.checkboxSelected.emit(event.target.checked);
  }

  onNewItemClick(): void {
    this.newItem.emit();
  }

  drop($event): void {
    const { previousIndex, currentIndex } = $event;

    this.dragDrop.emit({ previousIndex, currentIndex });
  }

  onSort(sortField): void {
    if (this.sortField === sortField) {
      this.sortDirection = this.sortDirection === 'Ascending' ? 'Descending' : 'Ascending';
    } else {
      this.sortDirection = 'Ascending';
    }

    this.sortField = sortField;

    this.sort.emit({ sortField, sortDirection: this.sortDirection });
  }

  trackColumn(index, item) {
    return item.label;
  }

  trackItem(index, item) {
    return item?.uId ?? item?.uid ?? index;
  }
}
